/* SPDX-License-Identifier: AGPL-3.0-or-later */

import {
  Component,
  computed,
  ElementRef,
  input,
  output,
  QueryList,
  viewChildren,
} from '@angular/core';
import {
  TeamDto,
  TournamentQualificationMatchDto,
  TournamentQualificationMatchParticipantDto,
} from '@api';
import { QualificationMatchLineComponent } from './qualification-match-line/qualification-match-line.component';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-qualification-matches',
  standalone: true,
  imports: [QualificationMatchLineComponent, MatIcon, MatButton],
  templateUrl: './qualification-matches.component.html',
  styleUrl: './qualification-matches.component.scss',
})
export class QualificationMatchesComponent {
  qualificationMatches = input.required<TournamentQualificationMatchDto[]>();
  tournamentId = input.required<number>();
  viewOnly = input<boolean>(false);
  teamDtos = input<Map<number, TeamDto>>(new Map());
  disableMatchEdit = input<boolean>(false);
  currentlyPlayingTeamIds = input<number[]>([]);
  sortByCurrentlyPlaying = input<boolean>(false);
  renderedRows = viewChildren<QueryList<ElementRef<HTMLTableRowElement>>>(
    'matchLineElement',
    // @ts-expect-error - this is a bug in the typescript definitions
    {
      read: ElementRef,
    },
  );
  generateQualificationMatches = output();
  onMatchUpdate = output<TournamentQualificationMatchDto>();
  markTeamDrinksPickedUp = output<MarkTeamDrinksPickedUpDto>();

  indexOfFirstNotFinishedGame = computed(() => {
    return this.sortedQualificationMatches.findIndex(m => !m.endTime);
  });

  showEmptyMessage = input<boolean>(true);
  sortPreviousMatchesDesc = input<boolean>(false);
  enableScrolling = input<boolean>(false);

  triggerMarkTeamDrinksPickedUp(
    match: TournamentQualificationMatchDto,
    participantDto: TournamentQualificationMatchParticipantDto,
  ) {
    this.markTeamDrinksPickedUp.emit({
      participantDto: participantDto,
      matchId: match.id!,
    });
  }

  get sortedQualificationMatches(): TournamentQualificationMatchDto[] {
    let result = structuredClone(this.qualificationMatches());
    result.sort((a, b) => {
      if(this.sortByCurrentlyPlaying()) {
        console.log(this.currentlyPlayingTeamIds())
        const aIsPlaying = this.currentlyPlayingTeamIds().includes(a.participants![0].teamId!) || this.currentlyPlayingTeamIds().includes(a.participants![1].teamId!);
        const bIsPlaying = this.currentlyPlayingTeamIds().includes(b.participants![0].teamId!) || this.currentlyPlayingTeamIds().includes(b.participants![1].teamId!);
        if(!aIsPlaying && bIsPlaying) {
          return -1;
        } else if(aIsPlaying && !bIsPlaying) {
          return 1;
        }
      }

      // first sort ended matches by end time
      if (a.endTime && b.endTime) {
        return new Date(a.endTime).valueOf() - new Date(b.endTime).valueOf();
      } else if (a.endTime) {
        return -1;
      } else if (b.endTime) {
        return 1;
      }

      // next, sort by running matches
      if (a.startTime && b.startTime) {
        return new Date(a.startTime).valueOf() - new Date(b.startTime).valueOf();
      } else if (a.startTime) {
        return -1;
      } else if (b.startTime) {
        return 1;
      }

      //next, by table assigned
      if (a.table && b.table) {
        //by name alphabetically
        return a.table.name!.localeCompare(b.table.name!);
      } else if (a.table) {
        return -1;
      } else if (b.table) {
        return 1;
      }

      // now, just whether none, one or both teams are ready
      const aDrinksCollected = (a.participants?.filter(p => p.isReady) || []).length;
      const bDrinksCollected = (b.participants?.filter(p => p.isReady) || []).length;
      return bDrinksCollected - aDrinksCollected;
    });

    if (this.sortPreviousMatchesDesc()) {
      const otherMatches = result.filter(m => !m.endTime);
      const previousMatches = result.filter(m => m.endTime).reverse();
      result = otherMatches.concat(previousMatches);
    }

    return result;
  }
}

export interface MarkTeamDrinksPickedUpDto {
  matchId: number;
  participantDto: TournamentQualificationMatchParticipantDto;
}
